import React, { useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import { ibeTransfersCommissionRead, ibeTransfersReportRead } from 'actions/cashup/beTransfersReport.action';
import Permitted from 'components/core/Permitted.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import CurrencySign from 'components/price/CurrencySign.component';
import SinglePayment from 'components/report/payment/SinglePayment.component';
import Title from 'components/report/payment/Title.component';
import PERM from 'constants/core/perm.const';
import ORDER from 'constants/order.const';
import STAFF_GROUP from 'constants/staff/group.const';
import useAef from 'hooks/useAef.hook';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import { isEnvMobile } from 'utils/env/determine.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isNotEmptyArray from 'utils/predicates/array/isNotEmptyArray.util';
import isPositiveNumber from 'utils/predicates/number/isPositiveNumber.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import byDate from 'utils/sorters/byDate.util';
import C from 'utils/values/constant.util';
import fixed from 'utils/values/fixed.util';

import CN from './Transfers.module.scss';

const L = logger('Transfers');

const byCreatedAtDescending = byDate({ key: 'created_at', order: ORDER.asc });

const COLUMN_NAMES = C({
  all: '',
  salon: 'cashUp.transfers.columnNames.business',
  employed: 'cashUp.transfers.columnNames.business',
  self: 'cashUp.transfers.columnNames.staff',
  single: 'cashUp.transfers.columnNames.staff',
});

const Transfers = ({
  ibeTransfersReportRead,
  begin,
  end,
  ids,
  intentIds,
  permission,
  groupName,
  selectedStylistId,
  hideModalForSubPaymentsGroup,
}) => {
  const { isDarkMode } = useTheme();
  const isMobile = isEnvMobile();
  const { t } = useTranslation();

  const [selectedIntentId, setSelectedIntentId] = useState(null);

  const { val: isModalRendered, yay: showModal, nay: hideModal } = useTip(false);

  const transfersRead = usePrepareReadOnly(ibeTransfersCommissionRead, { intentId: selectedIntentId });
  const transfersResult = transfersRead?.json;

  const { isLoading, value, error } = useAef(async () => {
    const result = await ibeTransfersReportRead({ bgn: begin, end, ids, intentIds, all: permission === 'all' });
    if (!result.is.ok) L.warn('', 'result:', result, error);
    return asObject(result).json;
  }, [ibeTransfersReportRead, begin, end, ids, intentIds, permission]);

  const handleOpenModal = (intentId) => {
    showModal();
    setSelectedIntentId(intentId);
  };

  const handleCloseModal = () => {
    hideModal();
    setSelectedIntentId(null);
  };

  const isSalon = STAFF_GROUP.salon === groupName;
  const isSelf = STAFF_GROUP.self === groupName;
  const isSingle = STAFF_GROUP.single === groupName;

  const isValidGroup = isSalon || isSelf || isSingle;

  const hasTransfersResult = transfersResult && isNotEmptyArray(transfersResult);

  const totalServiceAmount = isSalon
    ? hasTransfersResult
      ? transfersResult.reduce((sum, commission) => sum + commission.totalServiceAmount, 0)
      : '0.00'
    : isSelf
      ? hasTransfersResult
        ? transfersResult
            .filter((commission) => commission.stylistPercentage > 0)
            .reduce((sum, commission) => sum + commission.totalServiceAmount, 0)
        : '0.00'
      : isSingle
        ? hasTransfersResult
          ? transfersResult
              .filter((commission) => commission.stylistId === selectedStylistId) // only selectedStylistId in filter
              .reduce((sum, commission) => sum + commission.totalServiceAmount, 0)
          : '0.00'
        : '0.00';

  const totalAmount = isSalon
    ? hasTransfersResult
      ? transfersResult.reduce((sum, commission) => sum + commission.cardAmount, 0)
      : '0.00'
    : isSelf
      ? hasTransfersResult
        ? transfersResult
            .filter((commission) => commission.stylistPercentage > 0)
            .reduce((sum, commission) => sum + commission.cardAmount, 0)
        : '0.00'
      : isSingle
        ? hasTransfersResult
          ? transfersResult
              .filter((commission) => commission.stylistId === selectedStylistId) // only selectedStylistId in filter
              .reduce((sum, commission) => sum + commission.cardAmount, 0)
          : '0.00'
        : '0.00';

  const totalPercentage = isSalon
    ? hasTransfersResult
      ? (transfersResult.reduce((sum, commission) => sum + commission.businessRevenue, 0) /
          transfersResult.reduce((sum, commission) => sum + commission.cardAmount, 0)) *
        100
      : '0.00'
    : isSelf
      ? hasTransfersResult
        ? (transfersResult
            .filter((commission) => commission.stylistPercentage > 0)
            .reduce((sum, commission) => sum + commission.stylistRevenue, 0) /
            transfersResult
              .filter((commission) => commission.stylistPercentage > 0)
              .reduce((sum, commission) => sum + commission.cardAmount, 0)) *
          100
        : '0.00'
      : isSingle
        ? hasTransfersResult
          ? (transfersResult
              .filter((commission) => commission.stylistId === selectedStylistId) // only selected stylist
              .reduce((sum, commission) => sum + commission.stylistRevenue, 0) /
              transfersResult
                .filter((commission) => commission.stylistId === selectedStylistId)
                .reduce((sum, commission) => sum + commission.cardAmount, 0)) *
            100
          : '0.00'
        : '0.00';

  const safeTotalPercentage = isPositiveNumber(totalPercentage) ? totalPercentage : '0.00';

  const totalRevenue = isSalon
    ? hasTransfersResult
      ? transfersResult.reduce((sum, commission) => sum + commission.businessRevenue, 0)
      : '0.00'
    : isSelf
      ? hasTransfersResult
        ? transfersResult.reduce((sum, commission) => sum + commission.stylistRevenue, 0)
        : '0.00'
      : isSingle
        ? hasTransfersResult
          ? transfersResult
              .filter((commission) => commission.stylistId === selectedStylistId)
              .reduce((sum, commission) => sum + commission.stylistRevenue, 0)
          : '0.00'
        : '0.00';

  const infoMessage =
    isSingle &&
    transfersResult &&
    transfersResult.length > 0 &&
    transfersResult.some((c) => c.stylistId !== selectedStylistId);

  L.debug(
    'Transfers 💜',
    'Selected stylist ID from the cashup filter:',
    selectedStylistId,
    'group:',
    groupName,
    'intentIds',
    intentIds,
    'transfersResult:',
    transfersResult,
  );

  return (
    <div
      data-bem="Transfers"
      data-permission={permission}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="Transfers__constrictor" className={CN.constrictor}>
        <div className={CN.row}>
          <Title flexible width="small">
            {t('cashUp.transfers.columnNames.transactionDate')}
          </Title>
          <Title flexible width="small">
            {t('cashUp.transfers.columnNames.appointmentDate')}
          </Title>
          <Title flexible>{t('cashUp.transfers.columnNames.clientName')}</Title>
          <Title flexible>{t('cashUp.transfers.columnNames.clientAmount')}</Title>
          <Title flexible>{`${t(COLUMN_NAMES[groupName])} % ${t('cashUp.transfers.columnNames.share')}`}</Title>
          <Title flexible>
            {t(COLUMN_NAMES[groupName])} <CurrencySign /> {t('cashUp.transfers.columnNames.share')}
          </Title>
          <Title flexible>
            <Permitted value={PERM.canViewDailyCashupAllStaff}>
              {isValidGroup && !hideModalForSubPaymentsGroup ? '' : t('cashUp.transfers.columnNames.sentTo')}
            </Permitted>
            <Permitted value={PERM.canViewDailyCashupPersonal}>
              {!hideModalForSubPaymentsGroup ? '' : t('cashUp.transfers.columnNames.sentTo')}
            </Permitted>
          </Title>
        </div>

        {isLoading ? (
          <div className={CN.spinner}>
            <InlineSpinner />
          </div>
        ) : (
          <div className={CN.payoutsRow}>
            {asArray(value)
              .filter(alwaysSame)
              .sort(byCreatedAtDescending)
              .map((p, index) => (
                <SinglePayment
                  key={createNodeKey({ index, tag: p.id })}
                  payment={p}
                  intentIds={p.intent_id}
                  hideModalForStylistsGroup={isValidGroup}
                  hideModalForSubPaymentsGroup={hideModalForSubPaymentsGroup}
                  onOpenModal={() => handleOpenModal(p.intent_id)}
                />
              ))}
          </div>
        )}
      </div>

      <SalonModal tag="Transfers__modal" isOpen={isModalRendered} variant="large" banner="empty">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={handleCloseModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <div className={CN.messageContainer}>{t('cashUp.transfers.modal.header')}</div>

            {transfersResult ? (
              <div className={CN.tableTransfers}>
                <table data-bem="Transfers__table" className={CN.table}>
                  <thead>
                    <tr>
                      <th>{t('cashUp.transfers.modal.table.headers.item')}</th>
                      <th>{t('cashUp.transfers.modal.table.headers.commissionCategory')}</th>
                      <th>{t('cashUp.transfers.modal.table.headers.staffMember')}</th>
                      <th>{t('cashUp.transfers.modal.table.headers.totalPaid')}</th>
                      <th>{t('cashUp.transfers.modal.table.headers.paidCardSlickPay')}</th>
                      <th>
                        {isSalon
                          ? t('cashUp.transfers.modal.table.headers.businessShare')
                          : t('cashUp.transfers.modal.table.headers.staffShare')}
                      </th>
                      <th>
                        {isSalon ? (
                          <>
                            {t('cashUp.transfers.modal.table.headers.business')} <CurrencySign />{' '}
                            {t('cashUp.transfers.modal.table.headers.share')}
                          </>
                        ) : (
                          <>
                            {t('cashUp.transfers.modal.table.headers.staff')} <CurrencySign />{' '}
                            {t('cashUp.transfers.modal.table.headers.share')}
                          </>
                        )}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {transfersResult.map((commission, index) => {
                      const formattedCategory =
                        commission.commissionCategory === 'all_products'
                          ? t('cashUp.transfers.modal.table.body.allProducts')
                          : commission.commissionCategory === 'no_show'
                            ? t('cashUp.transfers.modal.table.body.noShowFee')
                            : commission.commissionCategory;

                      return (
                        <tr key={index}>
                          <td data-bem="Transfers__table--item">{commission.item}</td>
                          <td data-bem="Transfers__table--category">{formattedCategory}</td>
                          <td data-bem="Transfers__table--stylist">{commission.stylist}</td>
                          <td data-bem="Transfers__table--totalServiceAmount">
                            <CurrencySign value={commission.totalServiceAmount} />
                          </td>
                          <td data-bem="Transfers__table--cardAmount">
                            <CurrencySign value={commission.cardAmount} />
                          </td>
                          <td data-bem="Transfers__table--share-%">
                            <Permitted value={PERM.canViewDailyCashupAllStaff}>
                              {isSalon && fixed(commission.businessCommission * 100)}
                              {isSelf && fixed(commission.stylistPercentage * 100)}
                              {isSingle &&
                                (commission.stylistId === selectedStylistId
                                  ? fixed(commission.stylistPercentage * 100)
                                  : '0.00')}
                              %
                            </Permitted>
                            <Permitted value={PERM.canViewDailyCashupPersonal}>
                              {fixed(commission.stylistPercentage * 100)}%
                            </Permitted>
                          </td>
                          <td data-bem="Transfers__table--share-pound">
                            <Permitted value={PERM.canViewDailyCashupAllStaff}>
                              <CurrencySign />
                              {isSalon && fixed(commission.businessRevenue)}
                              {isSelf && fixed(commission.stylistRevenue)}
                              {isSingle &&
                                (commission.stylistId === selectedStylistId
                                  ? fixed(commission.stylistRevenue)
                                  : '0.00')}
                            </Permitted>
                            <Permitted value={PERM.canViewDailyCashupPersonal}>
                              <CurrencySign value={commission.stylistRevenue} />
                            </Permitted>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* NOTE:No footer
                   When PERM is canViewDailyCashupPersonal, we don't have the ID of the pre-selected stylist from the filter */}
                  <Permitted value={PERM.canViewDailyCashupAllStaff}>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className={CN.total}>
                          <strong>{t('common.total')}</strong>
                        </td>
                        <td data-bem="Transfers--total-totalServiceAmount" className={CN.total}>
                          <CurrencySign value={totalServiceAmount} />
                        </td>
                        <td data-bem="Transfers--total-cardAmount" className={CN.total}>
                          <CurrencySign value={totalAmount} />
                        </td>
                        <td data-bem="Transfers--total-%" className={CN.total}>
                          <CurrencySign value={safeTotalPercentage} />%
                        </td>
                        <td data-bem="Transfers--total-pound" className={CN.total}>
                          <CurrencySign value={totalRevenue} />
                        </td>
                      </tr>
                    </tfoot>
                  </Permitted>
                </table>
              </div>
            ) : (
              <InlineSpinner />
            )}
            {isMobile && <div className={CN.scrollHint}>{t('cashUp.transfers.modal.scrollRight')} →</div>}

            {infoMessage && (
              <div className={CN.warningMessage}>
                {t('cashUp.transfers.modal.messagePart1')} <CurrencySign />
                {t('cashUp.transfers.modal.messagePart2')}
              </div>
            )}
          </div>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

const TransfersHoc = connect(null, (dispatch) => bindActionCreators({ ibeTransfersReportRead }, dispatch))(Transfers);

export default TransfersHoc;
